import { ChevronsDownIcon, ChevronsUpIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMotionValue, animate } from "motion/react";
import { ConsoleLogsTab } from "./footer-tabs/console-logs-tab";
import { NetworkTab } from "./footer-tabs/network-tab";
import { cn } from "@/lib/utils";
import { ButtonGroup } from "@/components/ui/button-group";
import { Button } from "@/components/ui/button";
import { imagineStore } from "@/stores/imagine.store";
import { ServerLogsTab } from "./footer-tabs/server-logs-tab";
import {
  GlobeAltIcon,
  ServerIcon,
  CommandLineIcon,
} from "@heroicons/react/24/solid";
import { Resizable } from "re-resizable";
import { useLocalStorage } from "@uidotdev/usehooks";

function TabLabel({
  label,
  count,
}: {
  label: React.ReactNode;
  count?: number;
}) {
  return (
    <div className="flex items-center justify-center gap-1.5 min-w-22">
      {label}
      {count !== undefined && (
        <>
          {count > 0 && (
            <span className="text-[0.7rem] font-light text-foreground/50 bg-destructive/10 h-5 px-1 flex items-center justify-center rounded-md">
              {count}
            </span>
          )}
        </>
      )}
    </div>
  );
}

const FOOTER_HEADER_HEIGHT = 50;
const DEFAULT_FOOTER_EXPANDED_HEIGHT = 240;
const MIN_FOOTER_HEIGHT = 100;

export function CanvasPanelFooter() {
  const [currentTab, setCurrentTab] = useState<
    "console" | "network" | "server"
  >("console");
  const canvasFooterOpen = imagineStore.use.ui.canvasFooterOpen();
  const setCanvasFooterOpen = imagineStore.use.ui.setCanvasFooterOpen();
  const sandboxInstrumented = imagineStore.use.sandboxState.instrumented();
  const uiMode = imagineStore.use.ui.mode();
  const [localStorageOpenHeight, setLocalStorageOpenHeight] =
    useLocalStorage<number>(
      "canvas-footer-open-height",
      DEFAULT_FOOTER_EXPANDED_HEIGHT,
    );

  // Use motion value for height
  const initialHeight = canvasFooterOpen
    ? localStorageOpenHeight
    : FOOTER_HEADER_HEIGHT;
  const heightMotionValue = useMotionValue(initialHeight);
  const [currentHeight, setCurrentHeight] = useState(initialHeight);
  const isResizing = useRef(false);
  const animationRef = useRef<ReturnType<typeof animate> | null>(null);
  const resizeStartHeight = useRef(initialHeight);

  const consoleLogs = imagineStore.use.consoleLogs.logs();
  const numErrorLogs = useMemo(() => {
    return consoleLogs.filter((log) => log.level === "error").length;
  }, [consoleLogs]);
  const networkLogs = imagineStore.use.networkLogs.logs();
  const numNetworkErrors = useMemo(() => {
    return networkLogs.filter((i) => i.request?.error).length;
  }, [networkLogs]);

  const toggleCanvasFooter = () => {
    if (!sandboxInstrumented) {
      return;
    }
    setCanvasFooterOpen(!canvasFooterOpen);
  };

  // Animate height when footer opens/closes (only when not resizing)
  useEffect(() => {
    if (isResizing.current) return;

    const targetHeight = canvasFooterOpen
      ? localStorageOpenHeight
      : FOOTER_HEADER_HEIGHT;

    // Cancel any ongoing animation
    if (animationRef.current) {
      animationRef.current.stop();
    }

    // Use motion's animate function for smooth transitions
    animationRef.current = animate(heightMotionValue, targetHeight, {
      duration: 0.15,
      ease: "easeInOut",
      onUpdate: (latest) => {
        setCurrentHeight(latest);
      },
      onComplete: () => {
        heightMotionValue.set(targetHeight);
      },
    });

    return () => {
      if (animationRef.current) {
        animationRef.current.stop();
      }
    };
  }, [canvasFooterOpen, heightMotionValue, localStorageOpenHeight]);

  const handleResize = (
    _event: MouseEvent | TouchEvent,
    _direction: unknown,
    _ref: HTMLElement,
    delta: { height: number; width: number },
  ) => {
    // delta.height is cumulative from the start of resize, not incremental
    const newHeight = resizeStartHeight.current + delta.height;
    heightMotionValue.set(newHeight);
    setCurrentHeight(newHeight);
  };

  const handleResizeStart = () => {
    isResizing.current = true;
    // Store the height at the start of resize
    resizeStartHeight.current = heightMotionValue.get();
    // Stop any ongoing animation
    if (animationRef.current) {
      animationRef.current.stop();
    }
  };

  const handleResizeStop = (
    _event: MouseEvent | TouchEvent,
    _direction: unknown,
    _ref: HTMLElement,
    _delta: { height: number; width: number },
  ) => {
    isResizing.current = false;
    // Save the new height to localStorage
    const finalHeight = heightMotionValue.get();
    setLocalStorageOpenHeight(finalHeight);
  };

  return (
    <Resizable
      size={{
        height: currentHeight,
        width: "100%",
      }}
      onResizeStart={handleResizeStart}
      onResize={handleResize}
      onResizeStop={handleResizeStop}
      minHeight={canvasFooterOpen ? MIN_FOOTER_HEIGHT : undefined}
      enable={{
        top: canvasFooterOpen,
        right: false,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      className={cn(
        "items-center flex justify-between text-sm flex-col w-full z-20",
        uiMode === "preview" && "absolute bottom-0",
      )}
    >
      <div
        className={cn(
          "px-3 flex justify-between items-center bg-background w-full border-t",
          sandboxInstrumented && "cursor-pointer",
          canvasFooterOpen && "border-b",
        )}
        onClick={toggleCanvasFooter}
        style={{ height: FOOTER_HEADER_HEIGHT }}
      >
        <ButtonGroup
          options={[
            {
              value: "console",
              label: (
                <TabLabel
                  label={
                    <>
                      <CommandLineIcon className="size-4" />
                      Console
                    </>
                  }
                  count={numErrorLogs}
                />
              ),
            },
            {
              value: "network",
              label: (
                <TabLabel
                  label={
                    <>
                      <GlobeAltIcon className="size-4" />
                      Network
                    </>
                  }
                  count={numNetworkErrors}
                />
              ),
            },
            {
              value: "server",
              label: (
                <TabLabel
                  label={
                    <>
                      <ServerIcon className="size-4" />
                      Server
                    </>
                  }
                />
              ),
            },
          ]}
          disabled={!sandboxInstrumented}
          value={currentTab}
          onChange={(value) => {
            setCanvasFooterOpen(true);
            setCurrentTab(value as "console" | "network" | "server");
          }}
        />
        <Button
          size="icon"
          variant="ghost"
          onClick={toggleCanvasFooter}
          className={cn("cursor-pointer", !sandboxInstrumented && "hidden")}
          disabled={!sandboxInstrumented}
        >
          {canvasFooterOpen ? (
            <ChevronsDownIcon className="size-4" />
          ) : (
            <ChevronsUpIcon className="size-4" />
          )}
        </Button>
      </div>
      {canvasFooterOpen && (
        <div
          className={cn("w-full bg-neutral-800 flex-1 overflow-y-auto")}
          style={{
            height: `${currentHeight - FOOTER_HEADER_HEIGHT}px`,
            maxHeight: `${currentHeight - FOOTER_HEADER_HEIGHT}px`,
          }}
        >
          {currentTab === "console" && <ConsoleLogsTab />}
          {currentTab === "network" && <NetworkTab />}
          {currentTab === "server" && <ServerLogsTab />}
        </div>
      )}
    </Resizable>
  );
}
